import type { EarlyChildhoodAgeBand } from '../curriculum/early-childhood-provenance';
import type { ChildProfile } from './learning.models';

export interface EarlyChildhoodAgeOption {
  band: EarlyChildhoodAgeBand;
  label: string;
  approximateAge: 0 | 1 | 2;
}

export interface ProfileScreenPolicy {
  mode: 'caregiver-mediated' | 'child-direct';
  screenClassification: 'adult-coach-off-screen' | 'brief-co-use' | 'standard-child-session';
  childDirectAllowed: boolean;
  guidance: string;
}

export const EARLY_CHILDHOOD_AGE_OPTIONS: readonly EarlyChildhoodAgeOption[] = [
  { band: 'birth-1-week', label: 'Nascita – 1 settimana', approximateAge: 0 },
  { band: '1-week-6-months', label: '1 settimana – 6 mesi', approximateAge: 0 },
  { band: '6-9-months', label: '6 – 9 mesi', approximateAge: 0 },
  { band: '9-12-months', label: '9 – 12 mesi', approximateAge: 0 },
  { band: '12-24-months', label: '12 – 24 mesi', approximateAge: 1 },
  { band: '24-36-months', label: '24 – 36 mesi', approximateAge: 2 }
] as const;

export const SCHOOL_AGE_OPTIONS = [4, 5, 6, 7, 8] as const;

export function earlyChildhoodOption(
  band: EarlyChildhoodAgeBand | undefined
): EarlyChildhoodAgeOption | undefined {
  return EARLY_CHILDHOOD_AGE_OPTIONS.find((option) => option.band === band);
}

export function isEarlyChildhoodProfile(
  profile: Pick<ChildProfile, 'earlyChildhoodAgeBand'>
): boolean {
  return profile.earlyChildhoodAgeBand !== undefined;
}

export function profileAgeLabel(
  profile: Pick<ChildProfile, 'age' | 'earlyChildhoodAgeBand'>
): string {
  const option = earlyChildhoodOption(profile.earlyChildhoodAgeBand);
  return option?.label ?? `${profile.age} anni`;
}

export function profileScreenPolicy(
  profile: Pick<ChildProfile, 'earlyChildhoodAgeBand'>
): ProfileScreenPolicy {
  const band = profile.earlyChildhoodAgeBand;

  if (!band) {
    return {
      mode: 'child-direct',
      screenClassification: 'standard-child-session',
      childDirectAllowed: true,
      guidance: 'Attività interattive adatte al percorso scolastico selezionato.'
    };
  }

  if (band === '24-36-months') {
    return {
      mode: 'caregiver-mediated',
      screenClassification: 'brief-co-use',
      childDirectAllowed: false,
      guidance:
        'Il gioco reale resta principale. Eventuali interazioni dirette sullo schermo saranno abilitate solo da attività con provenienza 0–3 approvata.'
    };
  }

  return {
    mode: 'caregiver-mediated',
    screenClassification: 'adult-coach-off-screen',
    childDirectAllowed: false,
    guidance:
      'Il dispositivo guida l’adulto. Leggi la proposta, poi metti da parte lo schermo e svolgi l’attività insieme al bambino.'
  };
}
